<script lang="ts" setup>
import EditTravelOrderDialog from "@/views/trips/edit/dialogs/EditTravelOrderDialog.vue";
import TravelOrderStatusLabel from "@/views/trips/components/TravelOrderStatus.vue";
import { useValidation } from "@/composables/useValidation";
import type { RouteStopDto, TravelOrderDto } from "@/api/trips/dto/trip.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { computed, onMounted, ref, watch } from "vue";
import { RouteStopStatus, TravelOrderCancellationStatusMap } from "@/data/trip";

const { requiredValidator } = useValidation();

const travelOrder = defineModel<TravelOrderDto>();

const props = defineProps<{
  truck: TruckDto | null;
  routeStops: RouteStopDto[];
  travelOrderFromApi: TravelOrderDto | null;
  travelOrdersByOrders: Record<string, TravelOrderDto[]>;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: TravelOrderDto): void;
}>();

const rate = ref<string | undefined | number>(undefined);
const isEditDialog = ref<boolean>(false);

const travelOrderStatuses = computed(() => {
  return TravelOrderCancellationStatusMap;
});

const isCancelled = computed(() => {
  return travelOrderStatuses.value.some((s) => s.id === travelOrder.value?.status);
});

const canEditTravelOrder = computed(() => {
  const availableStatuses = [RouteStopStatus.inRoute, RouteStopStatus.notStarted, RouteStopStatus.waitingForLoading];
  const firstPickUp = props.routeStops[0];

  return availableStatuses.includes(firstPickUp.status);
});

watch(travelOrder, (value) => {
  if (value) {
    rate.value = value.rate;
  }
});

watch(rate, (value) => {
  if (travelOrder.value) {
    emits("update:modelValue", { ...travelOrder.value, rate: value });
  }
});

onMounted(() => {
  if (travelOrder.value) {
    rate.value = travelOrder.value.rate;
  }
});
</script>

<template>
  <div v-if="travelOrder" class="trip-block" data-qa="travel-order-block">
    <div :class="isCancelled ? 'bg-red-lighten-5' : 'bg-white'" class="trip-block__inner pa-4 rounded-lg">
      <div class="trip-block__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <span class="text-grey-darken-2 font-weight-medium">{{ travelOrder.name }}</span>
          <travel-order-status-label :travel-order="travelOrder" class="ml-2"></travel-order-status-label>
        </div>

        <v-menu offset-y>
          <template #activator="{ props: menuProps }">
            <v-btn elevation="0" icon="mdi-dots-horizontal" size="x-small" v-bind="menuProps" variant="text" />
          </template>
          <v-list dense>
            <v-tooltip :disabled="canEditTravelOrder">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <v-list-item :disabled="!canEditTravelOrder" @click="isEditDialog = true">
                    <v-list-item-title data-qa="cancel-travel-order">Edit travel order</v-list-item-title>
                  </v-list-item>
                </div>
              </template>

              Travel order can't be cancelled after loading at any stop
            </v-tooltip>
          </v-list>
        </v-menu>
      </div>
      <div>
        <v-text-field
          v-model="rate"
          :rules="[requiredValidator]"
          class="required"
          color="primary"
          data-qa="driver-rate"
          density="compact"
          hide-details="auto"
          label="Driver rate"
          type="number"
          variant="outlined"
        >
          <template #append-inner>
            <span class="font-size-16 text-grey-darken-1">$</span>
          </template>
        </v-text-field>
      </div>
    </div>

    <edit-travel-order-dialog
      v-if="isEditDialog"
      v-model="isEditDialog"
      v-model:travel-order="travelOrder"
      :route-stops="routeStops"
      :travel-order-from-api="travelOrderFromApi"
      :travel-orders-by-order="travelOrdersByOrders"
      :truck="truck"
    />
  </div>
</template>

<style scoped>
.trip-block__inner {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
